<template>
  <v-container class="pt-4">
    <div class="text-center mb-6">
      <h1 class="mb-0">{{ $t('rollOffWizard.disposal.title') }}</h1>
      <p class="mb-0">{{ $t('rollOffWizard.disposal.subtitle') }}</p>
    </div>
    <div>
      <v-row class="mb-8" align="stretch">
        <v-col
          v-for="option in options"
          :key="option.value"
          cols="12"
          sm="6"
          md="3"
          class="d-flex"
        >
          <v-card
            outlined
            flat
            class="rounded-lg pa-4 w-100 d-flex flex-column align-center disposal-card border-2-point-5"
            :style="wizardStore.selectedDisposal.includes(option.value) ? { borderColor: '#0b6f52' } : {}"
            @click="select(option.value)"
          >
            <v-img :src="option.img" class="no-drag mb-4" style="width:165px;height:165px;" :alt="$t(option.label)" />
            <div class="font-weight-bold mb-2 text-left" style="font-size:1.08rem;width:100%">{{ $t(option.label) }}</div>
            <div class="disposal-checkbox">
              <v-icon v-if="wizardStore.selectedDisposal.includes(option.value)" color="success">mdi-checkbox-marked</v-icon>
              <v-icon v-else color="#bdbdbd">mdi-checkbox-blank-outline</v-icon>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- popup warning -->
    <v-dialog v-model="wizardStore.showWarning" max-width="420" persistent transition="fade-transition" attach="attach">
      <v-card class="pa-4 rounded-lg">
        <v-row>
          <v-col cols="auto pb-0">
            <v-img
              class="pa-0"
              style="width: 130px;height: auto;"
              src="https://embed.wasteconnections.com/images-widget/roll-off-wizard/modal_warning-sign.png"
              :alt="$t('rollOffWizard.disposal.warningAlt')"
            />
          </v-col>
          <v-col>
            <p class="mt-4" style="font-weight: 500;font-size:19px;line-height: 1;">
              {{ $t('rollOffWizard.disposal.warningTitle') }}
            </p>
            <p style="font-size:15px;line-height: 1">
              {{ $t('rollOffWizard.disposal.warningBody') }}
            </p>
          </v-col>
        </v-row>
        <v-row justify="end" class="mt-1 pa-3">
          <v-btn class="service-bar-btn px-3" outlined height="40px" @click="wizardStore.nextView">
            <span class="service-bar-span">
              {{ $t('rollOffWizard.disposal.warningContinue') }}
            </span>
          </v-btn>
        </v-row>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script setup>
import { onMounted, onUnmounted } from 'vue'
import { useRollOffWizardStore } from '@/store/rollOffWizard'

const wizardStore = useRollOffWizardStore()

const options = wizardStore.projectType == "short-term" ? [
  { value: 'brick', label: 'rollOffWizard.disposal.options.bricks', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Residential/Brick.png' },
  { value: 'asphalt', label: 'rollOffWizard.disposal.options.asphalt', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Residential/Asphalt.png' },
  { value: 'stone', label: 'rollOffWizard.disposal.options.stone', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Residential/Stone.png' },
  { value: 'concrete', label: 'rollOffWizard.disposal.options.concrete', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Residential/Concrete.png' },
  { value: 'dirt', label: 'rollOffWizard.disposal.options.dirt', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Residential/Dirt.png' },
  { value: 'grass', label: 'rollOffWizard.disposal.options.grass', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Residential/Grass.png' },
  { value: 'shingles', label: 'rollOffWizard.disposal.options.shingles', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Residential/Shingles.png' },
  { value: 'woodflooring', label: 'rollOffWizard.disposal.options.woodFlooring', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Residential/Wood+flooring.png' },
  { value: 'tiles', label: 'rollOffWizard.disposal.options.tiles', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Residential/Tiles.png' },
  { value: 'drywall', label: 'rollOffWizard.disposal.options.drywall', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Residential/Drywall.png' },
  { value: 'insulation', label: 'rollOffWizard.disposal.options.insulation', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Residential/Insulation.png' },
  { value: 'furniture', label: 'rollOffWizard.disposal.options.furniture', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Residential/Furniture.png' }
] : [
  { value: 'metal', label: 'rollOffWizard.disposal.options.metal', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Commercial/Metal.png' },
  { value: 'wood', label: 'rollOffWizard.disposal.options.wood', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Commercial/Wood.png' },
  { value: 'plastic', label: 'rollOffWizard.disposal.options.plastic', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Commercial/Plastic.png' },
  { value: 'cardboard', label: 'rollOffWizard.disposal.options.cardboard', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Commercial/Cardboard.png' },
  { value: 'rock', label: 'rollOffWizard.disposal.options.rock', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Commercial/Stone.png' },
  { value: 'foil', label: 'rollOffWizard.disposal.options.foil', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Commercial/Aluminum.png' },
  { value: 'food', label: 'rollOffWizard.disposal.options.food', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Commercial/Food+waste.png' },
  { value: 'styrofoam', label: 'rollOffWizard.disposal.options.styrofoam', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Commercial/Styrofoam.png' },
  { value: 'pallets', label: 'rollOffWizard.disposal.options.pallets', img: 'https://embed.wasteconnections.com/images-widget/roll-off-wizard/Step4+(Materials)/Commercial/Pallets.png' }
]

function select(val) {
  const index = wizardStore.selectedDisposal.indexOf(val)
  if (index === -1) {
    wizardStore.selectedDisposal.push(val)
  } else {
    wizardStore.selectedDisposal.splice(index, 1)
  }
}

onMounted(() => {
  wizardStore.showWarning = false;
})

onUnmounted(() => {
  wizardStore.showWarning = false;
})
</script>

<style scoped>
.disposal-card {
  cursor: pointer;
  min-height: 220px;
  border-radius: 16px;
  position: relative;
  transition: border 0.2s, box-shadow 0.2s;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
  transition: ease 0.5s;
}
.disposal-card:hover {
  border-color: #003F6F;
}
.disposal-checkbox {
  position: absolute;
  top: 2px;
  right: 10px;
  font-size: 1.5rem;
}
::v-deep .service-bar-span {
  word-break: normal !important;
  font-size: 16px ;
  color: #003F6F !important;
  transition: 0.3s ease;
}
::v-deep .service-bar-btn {
  text-transform: none;
  border-radius: 7px !important;
  background-color: white !important;
  border-color: #003F6F !important;
  border-style: solid !important;
  transition: 0.3s ease;
}
::v-deep .service-bar-btn:hover {
  background-color: #003F6F !important;
}
::v-deep .service-bar-btn:hover .service-bar-span {
  color: white !important;
}
.no-drag{
  user-select: none; 
  user-drag: none;
  -webkit-user-drag: none;
  pointer-events: none;
}
</style>